/**
 * Modal para criar novo card na pipeline
 * Reutiliza hook de criação de card existente
 */

import { useState } from 'react';
import { Modal, Form, Input, InputNumber, message, Button } from 'antd';
import { useAuth } from '@/features/auth';
import { useCreateCard } from '../hooks/usePipelineCard';

interface PipelineNewCardModalProps {
  open: boolean;
  pipelineId: string;
  stageId: string;
  stageName?: string;
  onClose: () => void;
  onCardCreated?: () => void;
}

export function PipelineNewCardModal({
  open,
  pipelineId,
  stageId,
  stageName = 'Nova Etapa',
  onClose,
  onCardCreated,
}: PipelineNewCardModalProps) {
  const [form] = Form.useForm();
  const createCard = useCreateCard();
  const { profile } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleCreateCard = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      if (!profile?.uuid || profile.tenant_id == null) {
        throw new Error('Usuário não autenticado');
      }

      await createCard.mutateAsync({
        pipelineId,
        stageId,
        tenantId: Number(profile.tenant_id),
        responsavelId: profile.uuid,
        dadosCard: {
          nome: values.nome,
          telefone: values.telefone || '',
          interesse: values.interesse || '',
          valor: values.valor || 0,
        },
      });

      message.success('Card criado com sucesso!');
      form.resetFields();
      onCardCreated?.();
      onClose();
    } catch (error: any) {
      message.error(`Erro ao criar card: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={`Novo Card — ${stageName}`}
      open={open}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancelar
        </Button>,
        <Button
          key="create"
          type="primary"
          loading={loading}
          onClick={handleCreateCard}
        >
          Criar Card
        </Button>,
      ]}
      width={450}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark="optional"
        initialValues={{
          nome: '',
          telefone: '',
          interesse: '',
          valor: 0,
        }}
      >
        <Form.Item
          label="Nome"
          name="nome"
          rules={[
            { required: true, message: 'Nome é obrigatório' },
            { min: 2, message: 'Nome deve ter pelo menos 2 caracteres' },
          ]}
        >
          <Input
            placeholder="Nome do cliente/lead"
            size="large"
            autoFocus
          />
        </Form.Item>

        <Form.Item
          label="Telefone"
          name="telefone"
        >
          <Input
            placeholder="Telefone para contato"
            size="large"
          />
        </Form.Item>

        <Form.Item
          label="Interesse"
          name="interesse"
        >
          <Input.TextArea
            placeholder="Descreva o interesse do cliente"
            rows={3}
          />
        </Form.Item>

        <Form.Item
          label="Valor (R$)"
          name="valor"
        >
          <InputNumber
            min={0}
            step={100}
            size="large"
            formatter={(value) =>
              new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              }).format(value || 0)
            }
            parser={(value) => {
              const num = parseFloat(value?.replace(/\D/g, '') || '0');
              return (num / 100) as any;
            }}
            placeholder="0,00"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default PipelineNewCardModal;
